import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import api from "@/services/api";
import { useTranslation } from "@/context/TranslationContext";

const serviceOptions = [
  "SaaS Application",
  "Web & Mobile App",
  "Automation System",
  "Fintech Integration",
  "Trading System",
  "AI Tools & Agents",
  "Other / Not Sure",
];

const budgetOptions = [
  "Under $5,000",
  "$5,000 – $15,000",
  "$15,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000+",
  "Let's discuss",
];

const contactInfo = [
  {
    icon: "ri-mail-line",
    labelKey: "contact.info_email_label",
    valueKey: "contact.info_email_value",
    href: "mailto:info@schemackozo.com",
    color: "text-electric-blue",
    border: "border-electric-blue/20",
    bg: "bg-electric-blue/5",
  },
  {
    icon: "ri-time-line",
    labelKey: "contact.info_response_label",
    valueKey: "contact.info_response_value",
    href: null,
    color: "text-cyber-green",
    border: "border-cyber-green/20",
    bg: "bg-cyber-green/5",
  },
  {
    icon: "ri-global-line",
    labelKey: "contact.info_global_label",
    valueKey: "contact.info_global_value",
    href: null,
    color: "text-cyber-orange",
    border: "border-cyber-orange/20",
    bg: "bg-cyber-orange/5",
  },
  {
    icon: "ri-calendar-line",
    labelKey: "contact.info_call_label",
    valueKey: "contact.info_call_value",
    href: null,
    color: "text-electric-blue",
    border: "border-electric-blue/20",
    bg: "bg-electric-blue/5",
  },
];

export default function Contact() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setSubmitting(true);
    setError("");

    const formData = new FormData(form);
    const body = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      phone: (formData.get("phone") as string) || undefined,
      company: (formData.get("company") as string) || undefined,
      service_interest: (formData.get("service_interest") as string) || undefined,
      budget: (formData.get("budget") as string) || undefined,
      message: message,
    };

    try {
      await api.post("/leads", body);
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-black font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-16 px-4 md:px-12 lg:px-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20futuristic%20abstract%20technology%20background%20with%20glowing%20blue%20circuit%20patterns%2C%20digital%20communication%20network%20visualization%2C%20deep%20space%20atmosphere%2C%20electric%20blue%20light%20trails%2C%20cinematic%20dark%20sci-fi&width=1920&height=500&seq=contact-hero-bg-001&orientation=landscape"
            alt="Contact background"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-deep-black/60 to-deep-black" />
          <div className="absolute inset-0 bg-circuit-pattern opacity-40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("contact.hero_badge")}
            </span>
            <h1 className="font-black text-white leading-tight mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              {t("contact.hero_title")}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-cyber-blue">
                {t("contact.hero_highlight")}
              </span>
            </h1>
            <p className="text-silver text-base leading-relaxed">
              {t("contact.hero_description")}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: Contact Info */}
          <div className="lg:col-span-1 space-y-5">
            <h2 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("contact.info_title")}
            </h2>
            {contactInfo.map((info) => (
              <div key={info.labelKey} className={`border ${info.border} ${info.bg} rounded-2xl p-5`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-dark-card border border-dark-border mb-3 ${info.color}`}>
                  <i className={`${info.icon} text-lg`} />
                </div>
                <div className="text-silver-dim text-xs uppercase tracking-widest mb-1">{t(info.labelKey)}</div>
                {info.href ? (
                  <a href={info.href} className={`font-semibold text-sm ${info.color} hover:underline cursor-pointer`}>
                    {t(info.valueKey)}
                  </a>
                ) : (
                  <div className="font-semibold text-sm text-white">{t(info.valueKey)}</div>
                )}
              </div>
            ))}

            {/* Quick Links */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-5 mt-6">
              <h3 className="text-white font-bold text-sm mb-4">{t("contact.quick_links_title")}</h3>
              <div className="space-y-2">
                <Link to="/services" className="flex items-center gap-2 text-silver text-sm hover:text-electric-blue transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-electric-blue text-xs" />
                  {t("contact.quick_link_services")}
                </Link>
                <Link to="/portfolio" className="flex items-center gap-2 text-silver text-sm hover:text-electric-blue transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-electric-blue text-xs" />
                  {t("contact.quick_link_portfolio")}
                </Link>
                <Link to="/pricing" className="flex items-center gap-2 text-silver text-sm hover:text-electric-blue transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-electric-blue text-xs" />
                  {t("contact.quick_link_pricing")}
                </Link>
                <Link to="/about" className="flex items-center gap-2 text-silver text-sm hover:text-electric-blue transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-electric-blue text-xs" />
                  {t("contact.quick_link_about")}
                </Link>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-2">
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-cyber-green/10 border border-cyber-green/30 mx-auto mb-6">
                    <i className="ri-check-line text-3xl text-cyber-green" />
                  </div>
                  <h3 className="text-white font-black text-2xl mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {t("contact.success_title")}
                  </h3>
                  <p className="text-silver text-sm leading-relaxed max-w-sm mx-auto mb-8">
                    {t("contact.success_message")}
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-electric-blue text-sm font-medium hover:underline cursor-pointer"
                  >
                    {t("contact.submit_button")}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {t("contact.form_title")}
                  </h2>
                  {error && (
                    <div className="mb-4 p-3 rounded-xl bg-cyber-red/10 border border-cyber-red/30 text-cyber-red text-sm">
                      {error}
                    </div>
                  )}
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                          {t("contact.label_name")}
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder={t("contact.placeholder_name")}
                          className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                          {t("contact.label_email")}
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder={t("contact.placeholder_email")}
                          className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                          Company / Startup
                        </label>
                        <input
                          type="text"
                          name="company"
                          placeholder="Acme Inc."
                          className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                          Phone (Optional)
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="+1 234 567 8900"
                          className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                        {t("contact.label_service")}
                      </label>
                      <select
                        name="service_interest"
                        required
                        className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors cursor-pointer"
                      >
                        <option value="">{t("contact.option_select")}</option>
                        {serviceOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                        {t("contact.label_budget")}
                      </label>
                      <select
                        name="budget"
                        className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors cursor-pointer"
                      >
                        <option value="">{t("contact.budget_select")}</option>
                        {budgetOptions.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-silver text-xs font-medium uppercase tracking-widest mb-2">
                        {t("contact.label_message")}
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder={t("contact.placeholder_message")}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
                      />
                      <div className={`text-right text-xs mt-1 ${charCount > 450 ? "text-cyber-red" : "text-silver-dim"}`}>
                        {charCount}/500
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || charCount > 500}
                      className="w-full flex items-center justify-center gap-2 bg-electric-blue text-deep-black font-bold py-3.5 rounded-xl hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-deep-black/30 border-t-deep-black rounded-full animate-spin" />
                          {t("contact.submitting")}
                        </>
                      ) : (
                        <>
                          {t("contact.submit_button")}
                          <i className="ri-send-plane-line" />
                        </>
                      )}
                    </button>

                    <p className="text-silver-dim text-xs text-center">
                      {t("contact.privacy_note")}{" "}
                      <a href="/privacy" className="text-electric-blue hover:underline cursor-pointer">{t("contact.privacy_link")}</a>.
                      We never share your information.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

